import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';

const GetTeamPersonDelete = graphql(/* GraphQL */ `
  query GetTeamPersonDelete($teamId: uuid!, $personId: String!) {
    person_team_by_pk(team_id: $teamId, person_id: $personId) {
      team_id
      person_id
      team {
        id
        title
        permissions
      }
      person {
        id
        name
      }
    }
  }
`);

const DeleteTeamPerson = graphql(/* GraphQL */ `
  mutation DeleteTeamPerson($teamId: uuid!, $personId: String!) {
    delete_person_team_by_pk(person_id: $personId, team_id: $teamId) {
      person_id
      team_id
    }
  }
`);

export default function TeamPersonDelete() {
  const { personId, teamId } = useLocalSearchParams<{
    personId: string;
    teamId: string;
  }>();
  const userId = useUserId();
  const { dismissTo } = useRouter();
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetTeamPersonDelete,
    variables: { teamId, personId },
    requestPolicy: 'network-only',
  });
  const membership = data?.person_team_by_pk;
  const isSelf = personId === userId;
  const canRemove = Boolean(
    membership?.team.permissions?.[isSelf ? 'delete_self' : 'delete_others'],
  );
  const [{ fetching: removing }, deleteTeamPerson] =
    useMutation(DeleteTeamPerson);

  const onPressDelete = async () => {
    if (!canRemove || fetching || error || removing) return { success: false };
    const result = await deleteTeamPerson({ personId, teamId });
    if (result.error || !result.data?.delete_person_team_by_pk) {
      return { success: false };
    }
    return { success: true };
  };

  const onRemoved = () => {
    dismissTo(isSelf ? '/event' : { pathname: '/team', params: { teamId } });
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Remove Membership' }} />
      <ContentBoundary
        data={canRemove ? membership : null}
        empty={
          <EmptyPlaceholder message="This membership is unavailable or you do not have permission to remove it." />
        }
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        errorMessage="Could not load this membership. Please try again."
      >
        {(membership) => (
          <DestructiveAction
            key={`${teamId}:${personId}`}
            label="Remove membership"
            submittingLabel="Removing…"
            testID="TeamPersonConfirmRemove"
            onAction={onPressDelete}
            onSuccess={onRemoved}
          >
            <DestructiveActionHeading>
              Are you sure you want to remove {membership.person.name} from{' '}
              {membership.team.title}?
            </DestructiveActionHeading>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
